import DataTable from 'react-data-table-component';
import { Loader, Placeholder } from 'rsuite';
import dayjs from 'dayjs';
import { useMessages } from '../../../hooks';
import SafeHTML from '../../common/SafeHTML';

const LocalizedFormat = require('dayjs/plugin/localizedFormat');
dayjs.extend(LocalizedFormat);

export default function MessagesTable(props) {
	const {
		search = '',
		filter = {},
		title = 'Messages',
		messagingServiceSid,
		dateRange,
		broadcastId
	} = props;

	const {
		data,
		loading,
		totalRows,
		perPage,
		handlePageChange,
		handleSort,
		handlePerRowsChange
	} = useMessages({
		search,
		filter,
		dateRange,
		messagingServiceSid,
		broadcastId
	});

	const columns = [
		{
			name: 'Date',
			selector: (row) => row.sendAt,
			sortable: true,
			sortField: 'sendAt',
			filterable: true,
			format: (row) => {
				return dayjs(row.sendAt).format('LLL');
			}
		},
		{
			name: 'To',
			selector: (row) => row.to,
			format: (row) => {
				let detail = row.crmContactFirstName
					? `${row.crmContactFirstName} ${row.crmContactLastName} `
					: '';
				return detail.toUpperCase() + '(' + row.to + ')';
			}
		},
		{
			name: 'From',
			selector: (row) => row.messagingServiceName
		},
		{
			name: 'Status',
			selector: (row) => row.status,
			omit: true
		},
		{
			name: 'Message',
			selector: (row) => row.body,
			format: (row) => {
				return (
					row.body.substring(0, 60) +
					(row.body.length > 60 ? '...' : '')
				);
			}
		}
	];

	const ExpandedComponent = (data) => {
		return (
			<div className="message-container">
				<div className="message-header">
					<div>
						<strong>From: </strong>{' '}
						{data.data.from
							? data.data.from
							: data.data.messagingServiceName}
					</div>
					<div>
						<strong>To: </strong> {data.data.to}
					</div>
					<div>
						<strong>Status: </strong> {data.data.status}
					</div>
				</div>
				<SafeHTML
					className="message-body"
					html={data.data.body.replace(/(?:\r\n|\r|\n)/g, '<br />')}
				/>
			</div>
		);
	};

	const conditionalRowStyles = [
		{
			when: (row) =>
				['requested', 'accepted', 'queued', 'sending', 'sent'].indexOf(
					row.status
				) > -1,
			style: {
				backgroundColor: '#cfe2ff'
			}
		},
		{
			when: (row) => ['delivered'].indexOf(row.status) > -1,
			style: {
				backgroundColor: '#d1e7dd'
			}
		},
		{
			when: (row) => ['received'].indexOf(row.status) > -1,
			style: {
				backgroundColor: '#fff3cd'
			}
		},
		{
			when: (row) =>
				[
					'requested',
					'accepted',
					'queued',
					'sending',
					'sent',
					'received',
					'delivered',
					'scheduled'
				].indexOf(row.status) === -1,
			style: {
				backgroundColor: '#f8d7da'
			}
		}
	];

	return (
		<DataTable
			title={title}
			columns={columns}
			data={data}
			progressPending={loading}
			progressComponent={
				<div>
					<Placeholder.Paragraph rows={8} />
					<Loader backdrop content="Hang on..." vertical />
				</div>
			}
			paginationPerPage={perPage}
			pagination
			paginationServer
			paginationTotalRows={totalRows}
			defaultSortFieldId={1}
			defaultSortAsc={false}
			onChangeRowsPerPage={handlePerRowsChange}
			onChangePage={handlePageChange}
			onSort={handleSort}
			sortServer
			fixedHeader
			expandableRows
			expandableRowsComponent={ExpandedComponent}
			conditionalRowStyles={conditionalRowStyles}
		/>
	);
}
